import type { AuthoringLanguage } from '../api/coursesApi';

/**
 * Languages offered for NEW translations (CEE-1099). Mirrors the backend allowlist minus
 * `ar-SA`, which stays in the DB for existing rows but must not appear in the picker even
 * when the deployed API has not yet picked up the server-side filter.
 */
export const HIDDEN_AUTHORING_TRANSLATION_LANGUAGE_CODES = new Set(['ar-SA', 'ar-SS', 'ar']);

/**
 * English display labels for the Add translations picker (Figma 8.4 — node 17265:167643).
 * DB `languages.name` values differ (e.g. "French European", "Spanish (LatAM)").
 */
export const AUTHORING_TRANSLATION_LANGUAGE_LABELS: Record<string, string> = {
  'de-DE': 'German',
  'es-419': 'Spanish (Latin America)',
  'fr-FR': 'French',
  'fr-CA': 'French Canadian',
  'it-IT': 'Italian',
  'ja-JP': 'Japanese',
  'nl-NL': 'Dutch',
  'pt-BR': 'Portuguese Brazil',
  'zh-CN': 'Chinese (simplified)',
};

export function isHiddenAuthoringTranslationLanguage(code: string): boolean {
  return HIDDEN_AUTHORING_TRANSLATION_LANGUAGE_CODES.has(code);
}

export function getAuthoringTranslationLanguageLabel(
  code: string,
  fallbackName?: string | null,
): string {
  return AUTHORING_TRANSLATION_LANGUAGE_LABELS[code] ?? fallbackName ?? code;
}

export function filterAuthoringTranslationLanguages(
  languages: readonly AuthoringLanguage[],
): AuthoringLanguage[] {
  return languages.filter((language) => !isHiddenAuthoringTranslationLanguage(language.code));
}

export interface AddTranslationLanguageOption extends AuthoringLanguage {
  disabled: boolean;
}

/** Languages shown in Add translations — existing ones stay visible but cannot be selected again. */
export function getAddTranslationLanguageOptions(
  languages: readonly AuthoringLanguage[],
  existingLanguageCodes: readonly string[],
): AddTranslationLanguageOption[] {
  const existing = new Set(existingLanguageCodes);

  return filterAuthoringTranslationLanguages(languages)
    .map((language) => ({
      ...language,
      disabled: existing.has(language.code),
    }))
    .sort((left, right) =>
      getAuthoringTranslationLanguageLabel(left.code, left.name).localeCompare(
        getAuthoringTranslationLanguageLabel(right.code, right.name),
      ),
    );
}
